import { useMemo } from 'react'

import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import {
  getBedbankMetasearchPriceAccuracyEventData,
  getBedbankRatePublicTotal,
  getCheapestBedbankRateForMetasearchPriceAccuracy,
} from 'analytics/metasearchPriceAccuracyTracking'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useFireMetasearchPriceAccuracyEvent from 'hooks/OfferPage/useFireMetasearchPriceAccuracyEvent'
import useMetasearchDiscount from 'hooks/useMetasearchDiscount'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCheapestBedbankHotelOnlyRate, getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'

interface Params {
  checkInDate?: string
  checkOutDate?: string
  displayablePackages: Array<App.BedbankOfferPackageView>
  ghaPrice: string | null
  metasearchRoomRateId?: string
  offer: App.BedbankOffer
  rooms: Array<OfferPageRoom>
}

export default function useBedbankMetasearchPriceAccuracyTracking({
  checkInDate,
  checkOutDate,
  displayablePackages,
  ghaPrice,
  metasearchRoomRateId,
  offer,
  rooms,
}: Params) {
  // The price Google scrapes: cheapest public rate in the first visible room tile.
  const metasearchPriceAccuracyRate = useMemo(
    () => getCheapestBedbankRateForMetasearchPriceAccuracy(displayablePackages[0]?.rates ?? []),
    [displayablePackages],
  )
  // The rate the offer container actually fetched a metasearch discount for.
  const metasearchDiscountRateKey = useMemo(() => (
    checkInDate && checkOutDate ? getBedbankRateKey(rooms, checkInDate, checkOutDate) : undefined
  ), [checkInDate, checkOutDate, rooms])
  const metasearchDiscountRates = useAppSelector(state => (
    metasearchDiscountRateKey ? state.offer.bedbankOfferRates[offer.id]?.[metasearchDiscountRateKey] : undefined
  ))
  const metasearchDiscountRate = useMemo(() => (
    getCheapestBedbankHotelOnlyRate(metasearchDiscountRates ?? [])
  ), [metasearchDiscountRates])
  // Only apply the discount when the first-tile rate IS the discount-eligible rate; otherwise
  // track the first-tile price undiscounted so LPC/wrong-rate-first mismatches stay visible.
  const shouldApplyMetasearchDiscount = metasearchPriceAccuracyRate?.id === metasearchDiscountRate?.id

  const metasearchPriceAccuracyPrice = useMemo(() => {
    if (!metasearchPriceAccuracyRate) {
      return undefined
    }

    if (!checkInDate) {
      return getBedbankRatePublicTotal(metasearchPriceAccuracyRate)
    }

    // Same public display-price derivation the offer container uses for the discount base, so the
    // discount-request key (which includes price) matches and the discount resolves for our rate.
    return getDisplayTotalPrice({
      hotelPrice: getBedbankRatePublicTotal(metasearchPriceAccuracyRate),
      hotelMemberPrice: metasearchPriceAccuracyRate.totals.memberInclusive ?
        metasearchPriceAccuracyRate.totals.memberInclusive + (metasearchPriceAccuracyRate.totals.propertyFees ?? 0) :
        0,
      hotelValue: metasearchPriceAccuracyRate.value,
      checkIn: checkInDate,
      rooms,
      displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
    }).totals.price
  }, [checkInDate, metasearchPriceAccuracyRate, offer.displayPricingAsPerPerson, rooms])

  const [metasearchDiscountAmount, metasearchDiscountFetching, metasearchDiscountSettled] = useMetasearchDiscount(
    metasearchPriceAccuracyPrice ?? 0,
    {
      propertyId: offer.id,
      checkIn: checkInDate,
      checkOut: checkOutDate,
      rooms,
      disabled: !shouldApplyMetasearchDiscount,
    },
  )

  const eventData = useMemo(() => getBedbankMetasearchPriceAccuracyEventData({
    offerId: offer.id,
    pageType: 'legacy_bedbank',
    metasearchRoomRateId,
    rate: metasearchPriceAccuracyRate,
    price: metasearchPriceAccuracyPrice,
    discountAmount: metasearchDiscountAmount,
  }), [metasearchDiscountAmount, metasearchPriceAccuracyPrice, metasearchPriceAccuracyRate, metasearchRoomRateId, offer.id])

  useFireMetasearchPriceAccuracyEvent(
    eventData,
    ghaPrice === '1' && !metasearchDiscountFetching && metasearchDiscountSettled,
    `legacy_bedbank:${offer.id}`,
  )
}
